





























































































import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import ConfirmActionDialog from "./ConfirmActionDialog.vue";
import { IVideo } from "@/models/video.entity";
import { IPartnerImage } from "@/models/partner-image.entity";
import MediaGalleryVideoPlayer from "./MediaGalleryVideoPlayer.vue";

export enum MediaGalleryItemType {
  IMAGE = "image",
  VIDEO = "video"
}

export type MediaGalleryFullscreenItem =
  | { type: MediaGalleryItemType.IMAGE; medium: IPartnerImage }
  | { type: MediaGalleryItemType.VIDEO; medium: IVideo };

@Component({
  components: { ConfirmActionDialog, MediaGalleryVideoPlayer }
})
class MediaGalleryFullscreen extends Vue {
  @Prop({ required: true }) media!: MediaGalleryFullscreenItem[];
  @Prop({ required: true }) value!: boolean; // v-model
  @Prop({ default: 0 }) index!: number;
  @Prop({ default: false }) isDeleteDisabled!: boolean;
  @Prop({ default: false }) isDeleteLoading!: boolean;

  readonly MediaGalleryItemType = MediaGalleryItemType;

  localActive = false;
  localIndex = 0;

  showDeleteDialog = false;

  // current image zoom/pan
  private currentImageZoom: {
    imageId: string | null;
    scale: number;
    panX: number;
    panY: number;
    originX: number;
    originY: number;
    baseW: number;
    baseH: number;
  } = {
    imageId: null,
    scale: 1,
    panX: 0,
    panY: 0,
    originX: 50,
    originY: 50,
    baseW: 0,
    baseH: 0
  };

  private lastTapAt = 0;

  private panState: {
    imageId: string;
    pointerId: number;
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
    panEl: HTMLElement | null;
    pendingX: number;
    pendingY: number;
    raf: number | null;
  } | null = null;

  mounted() {
    this.localActive = this.value;
    this.localIndex = this.index || 0;
    window.addEventListener("keydown", this.onKeydown);
  }

  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
  }

  @Watch("value")
  onValueChanged(v: boolean) {
    this.localActive = v;
  }

  @Watch("localActive")
  onLocalActiveChanged(v: boolean) {
    this.$emit("input", v);
    if (!v) {
      // reset state when closing
      this.resetDomForCurrent();
      this.panState = null;
      this.lastTapAt = 0;
      this.setCurrentImageZoomTarget(null);
    } else {
      // opened: nothing special here; MediaVideoPlayer mounts itself
    }
  }

  @Watch("index")
  onIndexPropChanged(v: number) {
    this.localIndex = v || 0;
  }

  @Watch("localIndex")
  onLocalIndexChanged(v: number) {
    this.$emit("update:index", v);
    // reset DOM and zoom state when switching
    this.resetDomForCurrent();
    this.panState = null;
    this.lastTapAt = 0;
    const cur = this.currentItem;
    this.setCurrentImageZoomTarget(cur && cur.type === MediaGalleryItemType.IMAGE ? cur.medium.id : null);
  }

  get currentItem() {
    return (this.media || [])[this.localIndex] || null;
  }

  get fullscreenDisplayName() {
    const cur = this.currentItem;
    if (!cur) return "";
    if (cur.type === MediaGalleryItemType.IMAGE) return cur.medium.name || "";
    return cur.medium?.title || cur.medium.id || "";
  }

  get fullscreenDisplayType() {
    const cur = this.currentItem;
    if (!cur) return "";
    return cur.type === MediaGalleryItemType.IMAGE ? MediaGalleryItemType.IMAGE : MediaGalleryItemType.VIDEO;
  }

  get fullscreenTouchHandlers() {
    if (this.isImageZoomedLocal || this.panState) return {};
    return {
      left: this.goNext,
      right: this.goPrev,
      up: this.close,
      down: this.close
    };
  }

  get isImageZoomedLocal() {
    const cur = this.currentItem;
    if (!cur || cur.type !== MediaGalleryItemType.IMAGE) return false;
    return this.currentImageZoom.imageId === cur.medium.id && this.currentImageZoom.scale > 1;
  }

  private isTypingTarget(target: EventTarget | null) {
    const el = target as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    return el.isContentEditable === true;
  }

  private onKeydown(e: KeyboardEvent) {
    if (!this.localActive) return;
    if (this.isTypingTarget(e.target)) return;

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.goPrev();
      return;
    }

    if (e.key === "ArrowRight") {
      e.preventDefault();
      this.goNext();
    }
  }

  // DOM helpers
  private findFsImageEl(imageId: string) {
    const root = (this.$refs.fullscreenRoot as HTMLElement | undefined) || null;
    if (!root) return null;
    return root.querySelector(`img[data-fs-image-id="${imageId}"]`) as HTMLImageElement | null;
  }

  private applyZoomStyles(imageId: string, imgEl: HTMLImageElement | null) {
    if (!imgEl) return;
    const st = this.ensureCurrentImageZoomState(imageId);
    imgEl.style.transformOrigin = `${st.originX}% ${st.originY}%`;
    imgEl.style.transform = `scale(${st.scale})`;
  }

  private applyPanStyles(panEl: HTMLElement | null, x: number, y: number) {
    if (!panEl) return;
    panEl.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }

  private setCurrentImageZoomTarget(imageId: string | null) {
    this.currentImageZoom.imageId = imageId;
    this.currentImageZoom.scale = 1;
    this.currentImageZoom.panX = 0;
    this.currentImageZoom.panY = 0;
    this.currentImageZoom.originX = 50;
    this.currentImageZoom.originY = 50;
    this.currentImageZoom.baseW = 0;
    this.currentImageZoom.baseH = 0;
  }

  private ensureCurrentImageZoomState(imageId: string) {
    if (this.currentImageZoom.imageId !== imageId) this.setCurrentImageZoomTarget(imageId);
    return this.currentImageZoom;
  }

  private resetDomForCurrent() {
    const cur = this.currentItem;
    if (!cur || cur.type !== MediaGalleryItemType.IMAGE) return;
    const imgEl = this.findFsImageEl(cur.medium.id);
    if (imgEl) {
      imgEl.style.transformOrigin = "50% 50%";
      imgEl.style.transform = "scale(1)";
      const panEl = imgEl.parentElement as HTMLElement | null;
      if (panEl) panEl.style.transform = "translate3d(0px, 0px, 0)";
    }
  }

  // user actions
  close() {
    this.localActive = false;
  }

  goNext() {
    const len = (this.media || []).length;
    if (len <= 1) return;
    this.localIndex = (this.localIndex + 1) % len;
  }

  goPrev() {
    const len = (this.media || []).length;
    if (len <= 1) return;
    this.localIndex = (this.localIndex - 1 + len) % len;
  }

  openDeleteDialog() {
    if (this.isDeleteDisabled) return;
    if (!this.currentItem) return;
    this.showDeleteDialog = true;
  }

  confirmDelete() {
    this.showDeleteDialog = false;
    this.$emit("request-delete");
  }

  onStageDblClick(m: MediaGalleryFullscreenItem, e: MouseEvent) {
    if (!m || m.type !== MediaGalleryItemType.IMAGE) return;
    const imgEl = this.findFsImageEl(m.medium.id);
    this.toggleImageZoom(m.medium.id, e, imgEl);
  }

  onStageContextMenu(m: MediaGalleryFullscreenItem, e: MouseEvent) {
    if (!m || m.type !== MediaGalleryItemType.IMAGE) return;
    this.resetImageZoomHard(m.medium.id);
  }

  toggleImageZoom(imageId: string, e: MouseEvent, explicitImgEl?: HTMLImageElement | null) {
    const st = this.ensureCurrentImageZoomState(imageId);
    const imgEl = explicitImgEl ?? ((e.currentTarget as HTMLImageElement | null) || null);
    const rect = imgEl?.getBoundingClientRect();

    if (st.scale > 1) {
      st.scale = 1;
      st.panX = 0;
      st.panY = 0;
      st.originX = 50;
      st.originY = 50;
      st.baseW = rect?.width || st.baseW;
      st.baseH = rect?.height || st.baseH;

      if (imgEl) {
        imgEl.style.transformOrigin = "50% 50%";
        imgEl.style.transform = "scale(1)";
      }
      if (imgEl?.parentElement) this.applyPanStyles(imgEl.parentElement as HTMLElement, 0, 0);
      return;
    }

    if (rect) {
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      st.originX = Math.max(0, Math.min(100, x));
      st.originY = Math.max(0, Math.min(100, y));
      st.baseW = rect.width;
      st.baseH = rect.height;
    }
    st.scale = 2.5;
    st.panX = 0;
    st.panY = 0;

    this.applyZoomStyles(imageId, imgEl);
    if (imgEl?.parentElement) this.applyPanStyles(imgEl.parentElement as HTMLElement, 0, 0);
  }

  onImageTouchEnd(imageId: string, e: TouchEvent) {
    const now = Date.now();
    const isDoubleTap = now - this.lastTapAt <= 260;
    this.lastTapAt = now;
    if (!isDoubleTap) return;

    const touch = e.changedTouches && e.changedTouches[0];
    const st = this.ensureCurrentImageZoomState(imageId);
    const imgEl = (e.currentTarget as HTMLImageElement | null) || null;
    const rect = imgEl?.getBoundingClientRect();

    if (rect && touch) {
      const x = ((touch.clientX - rect.left) / rect.width) * 100;
      const y = ((touch.clientY - rect.top) / rect.height) * 100;
      st.originX = Math.max(0, Math.min(100, x));
      st.originY = Math.max(0, Math.min(100, y));
      st.baseW = rect.width;
      st.baseH = rect.height;
    }

    if (st.scale > 1) {
      st.scale = 1;
      st.panX = 0;
      st.panY = 0;
      st.originX = 50;
      st.originY = 50;

      if (imgEl) {
        imgEl.style.transformOrigin = "50% 50%";
        imgEl.style.transform = "scale(1)";
      }
      if (imgEl?.parentElement) this.applyPanStyles(imgEl.parentElement as HTMLElement, 0, 0);
      return;
    }

    st.scale = 2.5;
    st.panX = 0;
    st.panY = 0;

    this.applyZoomStyles(imageId, imgEl);
    if (imgEl?.parentElement) this.applyPanStyles(imgEl.parentElement as HTMLElement, 0, 0);
  }

  onImagePointerDown(imageId: string, e: PointerEvent) {
    if (!this.isImageZoomedLocal) return;
    const st = this.ensureCurrentImageZoomState(imageId);
    const imgEl = (e.currentTarget as HTMLImageElement | null) || null;
    const panEl = (imgEl?.parentElement as HTMLElement) || null;

    this.panState = {
      imageId,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startPanX: st.panX,
      startPanY: st.panY,
      panEl,
      pendingX: st.panX,
      pendingY: st.panY,
      raf: null
    };

    try {
      const target = e.currentTarget as HTMLElement | null;
      target?.setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
  }

  onImagePointerMove(imageId: string, e: PointerEvent) {
    const ps = this.panState;
    if (!ps) return;
    if (e.pointerId !== ps.pointerId) return;
    if (ps.imageId !== imageId) return;

    const dx = e.clientX - ps.startX;
    const dy = e.clientY - ps.startY;
    const st = this.ensureCurrentImageZoomState(imageId);

    let nextX = ps.startPanX + dx;
    let nextY = ps.startPanY + dy;

    const rect = (e.currentTarget as HTMLElement | null)?.getBoundingClientRect();
    if (rect) {
      st.baseW = rect.width;
      st.baseH = rect.height;
    }

    if (st.scale <= 1 || !st.baseW || !st.baseH) {
      nextX = 0;
      nextY = 0;
    } else {
      const maxX = (st.baseW * st.scale - st.baseW) / 2;
      const maxY = (st.baseH * st.scale - st.baseH) / 2;
      nextX = Math.max(-maxX, Math.min(maxX, nextX));
      nextY = Math.max(-maxY, Math.min(maxY, nextY));
    }

    ps.pendingX = nextX;
    ps.pendingY = nextY;
    if (ps.raf == null) {
      ps.raf = window.requestAnimationFrame(() => {
        if (!this.panState || this.panState !== ps) return;
        ps.raf = null;
        this.applyPanStyles(ps.panEl, ps.pendingX, ps.pendingY);
      });
    }
  }

  onImagePointerUp(imageId: string, e: PointerEvent) {
    const ps = this.panState;
    if (!ps) return;
    if (e.pointerId !== ps.pointerId) return;
    if (ps.imageId !== imageId) return;

    const st = this.ensureCurrentImageZoomState(imageId);
    st.panX = ps.pendingX;
    st.panY = ps.pendingY;

    if (ps.raf != null) {
      try {
        window.cancelAnimationFrame(ps.raf);
      } catch {
        // ignore
      }
    }

    this.panState = null;
  }

  onImageWheel(imageId: string, e: WheelEvent) {
    if (!this.isImageZoomedLocal) return;
    const st = this.ensureCurrentImageZoomState(imageId);

    const imgEl = (e.currentTarget as HTMLImageElement | null) || null;
    const rect = imgEl?.getBoundingClientRect();
    if (rect) {
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      st.originX = Math.max(0, Math.min(100, x));
      st.originY = Math.max(0, Math.min(100, y));
      st.baseW = rect.width;
      st.baseH = rect.height;
    }

    const factor = Math.exp(-e.deltaY / 300);
    const nextScale = Math.max(1, Math.min(6, st.scale * factor));
    st.scale = nextScale;

    // Keep pan valid for new zoom range
    if (st.scale <= 1) {
      st.panX = 0;
      st.panY = 0;
    }

    this.applyZoomStyles(imageId, imgEl);
    const effectivePanX = st.scale <= 1 ? 0 : st.panX;
    const effectivePanY = st.scale <= 1 ? 0 : st.panY;
    this.applyPanStyles(imgEl?.parentElement as HTMLElement, effectivePanX, effectivePanY);
  }

  onImageContextMenu(imageId: string, e: MouseEvent) {
    this.resetImageZoomHard(imageId, (e.currentTarget as HTMLImageElement | null) || null);
  }

  onFullscreenContextMenu(e: MouseEvent) {
    const target = e.target as HTMLElement | null;
    if (target?.closest?.(".fullscreen-toolbar") || target?.closest?.(".fs-nav")) return;
    const cur = this.currentItem;
    if (!cur || cur.type !== MediaGalleryItemType.IMAGE) return;
    this.resetImageZoomHard(cur.medium.id);
  }

  private resetImageZoomHard(imageId: string, explicitImgEl?: HTMLImageElement | null) {
    const st = this.ensureCurrentImageZoomState(imageId);

    const hasNonDefault = st.scale !== 1 || st.panX !== 0 || st.panY !== 0 || st.originX !== 50 || st.originY !== 50;
    if (!hasNonDefault) return;

    st.scale = 1;
    st.panX = 0;
    st.panY = 0;
    st.originX = 50;
    st.originY = 50;

    const imgEl = explicitImgEl ?? this.findFsImageEl(imageId);
    if (imgEl) {
      imgEl.style.transformOrigin = "50% 50%";
      imgEl.style.transform = "scale(1)";
      if (imgEl.parentElement) this.applyPanStyles(imgEl.parentElement as HTMLElement, 0, 0);
    } else {
      this.resetDomForCurrent();
    }
  }

  isImageZoomed(imageId: string) {
    return this.currentImageZoom.imageId === imageId && this.currentImageZoom.scale > 1;
  }
}

export default MediaGalleryFullscreen;
